/**
 * Lightweight analytics.
 *
 * Events stream into the Lovable Cloud `analytics_events` table (always on,
 * no PII), and ALSO into GA4 via gtag once the visitor has accepted
 * analytics cookies. GA4 itself is consent-gated by Google Consent Mode v2
 * defaults set in index.html plus the CookieConsent component.
 */
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "aa_session_id";
const CONSENT_KEY = "aa_consent_v1";
export const GA_MEASUREMENT_ID = "G-D3KG8WTYLV";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    __ga4_loaded?: boolean;
  }
}

type ConsentChoice = { analytics: boolean; ts: number };

export const readConsent = (): ConsentChoice | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? (JSON.parse(raw) as ConsentChoice) : null;
  } catch {
    return null;
  }
};

export const writeConsent = (analytics: boolean): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ analytics, ts: Date.now() }),
    );
  } catch {
    /* ignore */
  }
};

export const hasAnalyticsConsent = (): boolean => !!readConsent()?.analytics;

/** Inject the GA4 loader script. Idempotent. */
export const initGA4 = (): void => {
  if (typeof window === "undefined") return;
  if (window.__ga4_loaded) return;
  window.__ga4_loaded = true;

  window.gtag?.("consent", "update", {
    analytics_storage: "granted",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Configure GA4. We send pageviews manually on route change.
  window.gtag?.("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });

  // Fire the initial pageview now that GA4 is live.
  trackPageview(window.location.pathname + window.location.search, document.title);
};

/** Revoke analytics consent in gtag. Cannot unload the script; the consent
 *  signal stops further data being recorded. */
export const revokeGA4 = (): void => {
  if (typeof window === "undefined") return;
  window.gtag?.("consent", "update", { analytics_storage: "denied" });
};

const getSessionId = (): string => {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        (crypto.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
};

export type AnalyticsProps = Record<string, unknown>;

const sendToGA4 = (eventName: string, properties: AnalyticsProps): void => {
  if (typeof window === "undefined") return;
  if (!window.gtag) return;
  if (!hasAnalyticsConsent()) return;
  try {
    window.gtag("event", eventName, properties);
  } catch {
    /* never throw */
  }
};

export const trackEvent = (
  eventName: string,
  properties: AnalyticsProps = {},
): void => {
  if (typeof window === "undefined") return;

  // Mirror to GA4 (consent-gated).
  sendToGA4(eventName, properties);

  // Always log to first-party store. Fire-and-forget.
  const payload = {
    event_name: eventName,
    page_path: window.location.pathname + window.location.search,
    properties: properties as never,
    session_id: getSessionId(),
    user_agent: navigator.userAgent,
    referrer: document.referrer || null,
  };

  void supabase
    .from("analytics_events")
    .insert(payload)
    .then(({ error }) => {
      if (error && import.meta.env.DEV) {
        console.warn("[analytics] insert failed", error.message);
      }
    });
};

/** GA4 SPA pageview. No-op until consent is granted. */
export const trackPageview = (path: string, title: string): void => {
  if (typeof window === "undefined") return;
  if (!window.gtag) return;
  if (!hasAnalyticsConsent()) return;
  try {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.origin + path,
      page_title: title,
      send_to: GA_MEASUREMENT_ID,
    });
  } catch {
    /* ignore */
  }
};

/** Convenience wrapper for CTA clicks. Use as an onClick handler factory. */
export const trackCta = (ctaId: string, location: string, extra: AnalyticsProps = {}) =>
  () => trackEvent("cta_click", { cta_id: ctaId, location, ...extra });
